import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "loading-spinner"
}
const _hoisted_2 = { key: 1 }

import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter, RouteLocationRaw } from 'vue-router';
import { useQuery } from '@vue/apollo-composable';
import { bucketUserRole } from '@/utils/bucket-utils';
import { deepParseDates } from '@/utils/date-utils';
import { BucketDocument, BucketType } from '@/gql/graphql';
import DataBrowser from '@/components/data/DataBrowser.vue';
import { useUserStore } from '@/store/user';
import { SHARING_USER_ROLES, BUCKET_DATA_TYPES } from '@/constants';
import { BAlert, BButton, BSpinner } from 'bootstrap-vue-next';
import { openRouterLink } from '@/utils/router-utils';

// Get the current route and router.

export default /*@__PURE__*/_defineComponent({
  __name: 'BucketDetail',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const pathPrefix = ref('');
const userStore = useUserStore();

// --- Bucket ID and Prefix from route ---
// Compute the numeric projectId by converting the route param to a number
const bucketId = computed(() => {
  const id = route.params.id as string;
  const parsedId = Number(id);
  if (isNaN(parsedId)) {
    throw new Error('Invalid bucket ID');
  }
  return parsedId;
});

// update the URL's 'path' parameter when changed
watch(pathPrefix, (newPath) => {
  router.replace({
    query: { ...route.query, path: newPath },
  });
});

// set the current folder path if 'path' present
onMounted(() => {
  if (route.query.path) {
    pathPrefix.value = String(route.query.path);
  }
});

// Query helper to parse boolean query parameters with a default
const qBool = (key: string, def = false) => {
  const v = route.query[key];
  if (v == null) return def;
  const s = Array.isArray(v) ? v[0] : v;
  return s === '1' || s === 'true';
};

// --- Go Back Link ---
const backLinkTarget = computed<RouteLocationRaw>(() => {
  const q = route.query;
  if (q.returnTo) {
    const path = decodeURIComponent(String(q.returnTo));
    return { path }; // robust even if it has its own query/hash
  }
  // sensible fallback if user loaded the page directly
  return { name: 'DataManager' };
});

const backLinkLabel = computed(() => {
  const q = route.query;
  if (q.returnLabel) {
    return `Back to ${String(q.returnLabel)}`;
  }
  // sensible fallback if user loaded the page directly
  return 'Go Back';
});

// props from query with sensible defaults
const showMetadata = computed(() => qBool('showMetadata', true));
const allowDownload = computed(() => qBool('allowDownload', true));
const listComputeTasks = computed(() => qBool('listComputeTasks', true));
const listSamples = computed(() => qBool('listSamples', true));
const listProjects = computed(() => qBool('listProjects', true));
const listUsers = computed(() => qBool('listUsers', true));

// --- Query Bucket ---
const {
  result: bucketQueryResult,
  loading: bucketQueryInProgress,
  error: bucketQueryError,
} = useQuery(BucketDocument, { id: bucketId.value });

// --- Buckets ---
const bucket = computed<BucketType | null>(() => {
  if (!bucketQueryResult.value?.bucket) {
    return null;
  }
  const bucketData = bucketQueryResult.value.bucket;
  return deepParseDates(bucketData) as BucketType;
});

const userRole = computed(() => {
  if (!bucket.value) {
    return SHARING_USER_ROLES.VIEWER;
  } else if (bucket.value.dataType === BUCKET_DATA_TYPES.SYSTEM) {
    return SHARING_USER_ROLES.EDITOR;
  }
  const role = bucketUserRole(userStore.id, bucket.value);
  if (role === null) {
    return SHARING_USER_ROLES.VIEWER;
  }
  return role;
});

// --- handlers of the UI components ---
function onBucketDeleted() {
  router.push(backLinkTarget.value);
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BButton), {
      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(openRouterLink)(backLinkTarget.value, _ctx.$router, $event))),
      variant: "outline-dark",
      class: "mb-3",
      size: "sm"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "angle-left",
          class: "me-2"
        }),
        _createTextVNode(" " + _toDisplayString(backLinkLabel.value), 1)
      ]),
      _: 1
    }),
    (_unref(bucketQueryInProgress))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[3] || (_cache[3] = _createTextVNode(" Loading... ", -1)),
          _createVNode(_unref(BSpinner), { small: "" })
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(bucketQueryError),
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(bucketQueryError) ? (bucketQueryError).value = $event : null)),
      variant: "danger"
    }, {
      default: _withCtx(() => [
        _createTextVNode(" Error loading bucket: " + _toDisplayString(_unref(bucketQueryError)?.message), 1)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (!_unref(bucketQueryInProgress) && !_unref(bucketQueryError) && bucket.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(DataBrowser, {
            "bucket-id": bucketId.value,
            "show-metadata": showMetadata.value,
            role: userRole.value,
            "allow-download": allowDownload.value,
            "list-compute-tasks": listComputeTasks.value,
            "list-samples": listSamples.value,
            "list-projects": listProjects.value,
            "list-users": listUsers.value,
            pathPrefix: pathPrefix.value,
            "onUpdate:pathPrefix": _cache[2] || (_cache[2] = ($event: any) => ((pathPrefix).value = $event)),
            onBucketDeleted: onBucketDeleted
          }, null, 8, ["bucket-id", "show-metadata", "role", "allow-download", "list-compute-tasks", "list-samples", "list-projects", "list-users", "pathPrefix"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})